import type { MessagePart } from '@elara/domain/models/schemas/message-schemas';
import type { IconName } from '@elara/client-core/ui-contracts/icon-name';

import type { ToolCall } from '#models/ToolCallModel.js';

import { isToolRequestPart } from '@elara/domain/models/schemas/message-schemas';

import { findToolBadgeSpec } from './registry.js';
import { resolveToolBadgeContent } from './view-model.js';

export const TOOL_BADGE_GROUP_THRESHOLD = 2;

export interface ToolBadgeGroupViewModel {
  icon: IconName;
  toolCalls: ToolCall[];
  summary: string;
  shouldGroup: boolean;
}

const MIXED_TOOL_GROUP_ICON: IconName = 'fact_check';
const COMBINABLE_ACTIVITY_PREFIXES = [
  'Added items to your ',
  'Adding items to your ',
  'Removed items from your ',
  'Removing items from your ',
  'Searched for ',
  'Searching for ',
] as const;

/** Reads a workflow run id without depending on the tool's untyped input/output shape. */
function readRunId(source: unknown, field: 'runId' | 'run_id'): string | undefined {
  if (!source || typeof source !== 'object') return undefined;
  const value = (source as Record<string, unknown>)[field];
  return typeof value === 'string' && value !== '' ? value : undefined;
}

function joinActivityLabels(labels: readonly string[]): string {
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0]!;
  const remaining = labels.slice(1).map((label) => label.toLowerCase());
  if (remaining.length === 1) return `${labels[0]} and ${remaining[0]}`;
  return `${labels[0]}, ${remaining.slice(0, -1).join(', ')}, and ${remaining.at(-1)}`;
}

function limitActivityLabels(labels: string[], hasActiveCall: boolean): string[] {
  const maximumNamedActivities = 3;
  if (labels.length <= maximumNamedActivities) return labels;
  const remaining = labels.length - 2;
  return [...labels.slice(0, 2), `${hasActiveCall ? 'Working on' : 'Completed'} ${remaining} more actions`];
}

function combineRelatedActivities(labels: string[]): string[] {
  const result = [...labels];
  for (const prefix of COMBINABLE_ACTIVITY_PREFIXES) {
    const indexes = result.flatMap((label, index) => (label.startsWith(prefix) ? [index] : []));
    if (indexes.length < 2) continue;
    const insertAt = Math.min(...indexes);
    const combined = `${prefix}${joinActivityLabels(indexes.map((index) => result[index]!.slice(prefix.length)))}`;
    for (const index of indexes.toSorted((a, b) => b - a)) result.splice(index, 1);
    result.splice(insertAt, 0, combined);
  }
  return result;
}

function toolActivity(toolCall: ToolCall): { key: string; label: string; isActive: boolean } {
  const spec = findToolBadgeSpec(toolCall.toolName);
  const key = spec?.activitySummary?.key ?? spec?.toolName ?? toolCall.toolName;
  const label = spec?.activitySummary?.label?.(toolCall, toolCall.isActive) ?? spec?.label(toolCall, toolCall.isActive);
  return {
    key,
    label: label?.replaceAll(/\[[^\]]+\]\([^)]*\)/g, 'an item') ?? 'Used a tool',
    isActive: toolCall.isActive,
  };
}

function resolveGroupIcon(toolCalls: readonly ToolCall[]): IconName {
  const specs = toolCalls.map((toolCall) => findToolBadgeSpec(toolCall.toolName));
  const canonicalToolNames = new Set(toolCalls.map((toolCall, index) => specs[index]?.toolName ?? toolCall.toolName));
  if (canonicalToolNames.size !== 1) return MIXED_TOOL_GROUP_ICON;
  return specs[0]?.icon ?? MIXED_TOOL_GROUP_ICON;
}

/**
 * Resolves the traveler-visible tool calls in message order and builds the one-line summary shared by
 * web and mobile. Repeated semantic activities affect the expanded list, but appear only once in the
 * summary. Related room-workflow stages are suppressed only when their run ids prove they are the
 * same operation.
 */
export function resolveToolBadgeGroup(
  content: readonly MessagePart[],
  toolCalls: readonly ToolCall[],
  excludedToolCallIds: ReadonlySet<string> = new Set(),
): ToolBadgeGroupViewModel {
  const byId = new Map(toolCalls.map((toolCall) => [toolCall.id, toolCall]));
  const supersededFetchRunIds = new Set<string>();
  for (const part of content) {
    if (!isToolRequestPart(part)) continue;
    const toolCall = part.toolRequest.ref == null ? undefined : byId.get(part.toolRequest.ref);
    if (toolCall?.toolName !== 'resumeRoomSearchWorkflow') continue;
    const runId = readRunId(toolCall.request?.input, 'run_id');
    if (runId) supersededFetchRunIds.add(runId);
  }

  const visible: ToolCall[] = [];
  const seenIds = new Set<string>();
  for (const part of content) {
    if (!isToolRequestPart(part) || part.toolRequest.ref == null) continue;
    const toolCall = byId.get(part.toolRequest.ref);
    if (!toolCall || seenIds.has(toolCall.id) || excludedToolCallIds.has(toolCall.id)) continue;
    seenIds.add(toolCall.id);
    if (toolCall.extendedStatus === 'failed' || resolveToolBadgeContent(toolCall).kind === 'hidden') continue;
    if (toolCall.toolName === 'fetchRoomAvailability') {
      const fetchRunId = readRunId(toolCall.output, 'runId');
      if (fetchRunId && supersededFetchRunIds.has(fetchRunId)) continue;
    }
    visible.push(toolCall);
  }

  const activities = new Map<string, ReturnType<typeof toolActivity>>();
  for (const toolCall of visible) {
    const activity = toolActivity(toolCall);
    const existing = activities.get(activity.key);
    if (!existing || (!existing.isActive && activity.isActive)) activities.set(activity.key, activity);
  }
  const uniqueLabels = combineRelatedActivities([...activities.values()].map((activity) => activity.label));
  const summary = joinActivityLabels(
    limitActivityLabels(
      uniqueLabels,
      visible.some((toolCall) => toolCall.isActive),
    ),
  );
  return {
    icon: resolveGroupIcon(visible),
    toolCalls: visible,
    summary,
    shouldGroup: visible.length >= TOOL_BADGE_GROUP_THRESHOLD,
  };
}
